import { useTranslation } from "react-i18next";
import { useGetLockoutBanner } from "@/api/orval/generated/lockout-banner";
import { useGetMyOrganization } from "@/api/orval/generated/organization";
import { LockoutBannerSeverity } from "@/api/orval/generated/schemas";
import { buttonVariants } from "@/components/ui/button";
import { useLocale } from "@/hooks/use-locale";
import { NAMESPACES } from "@/i18n/constants";
import { ICON_KEYS, iconRegistry } from "@/icons";
import { useSelectedAccount } from "@/services/session-manager/use-selected-account";
import { cn } from "@/utils/class-name";

const WarningIcon = iconRegistry[ICON_KEYS.WARNING];
const ExternalLinkIcon = iconRegistry[ICON_KEYS.EXTERNAL_LINK];

// Dates are calendar days; formatting at UTC midnight in UTC keeps the day from shifting with the viewer's zone.
const formatCalendarDate = (date: string | undefined, locale: string) =>
    date &&
    new Intl.DateTimeFormat(locale, { dateStyle: "long", timeZone: "UTC" }).format(new Date(`${date}T00:00:00Z`));

export function OrganizationLockoutBanner() {
    const { t } = useTranslation(NAMESPACES.FEATURES.NAVIGATION, { keyPrefix: "lockout-banner" });
    const locale = useLocale();
    const account = useSelectedAccount();
    const { data: organization } = useGetMyOrganization({ query: { enabled: Boolean(account) } });
    const organizationVerdiId = organization?.verdiId ?? "";
    const { data: banner } = useGetLockoutBanner(organizationVerdiId, {
        query: { enabled: Boolean(account && organizationVerdiId) },
    });

    if (!account || !banner?.enabled) {
        return null;
    }

    const isCritical = banner.severity === LockoutBannerSeverity.CRITICAL;

    return (
        <div
            className={cn(
                "flex w-full flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 py-1.5 text-center text-sm",
                isCritical ? "bg-red-700 text-white" : "bg-warning text-black",
            )}
            role={"alert"}
        >
            <p>
                <WarningIcon className={"mr-1.5 inline size-4 align-text-bottom"} />
                {banner.messageKey ? (
                    <>
                        <strong>{t(`messages.${banner.messageKey}.title`)}</strong>{" "}
                        {t(`messages.${banner.messageKey}.body`, {
                            context: banner.date ? "dated" : undefined,
                            date: formatCalendarDate(banner.date, locale),
                        })}
                    </>
                ) : (
                    banner.customMessage
                )}
            </p>
            <a
                className={cn(
                    buttonVariants({
                        className: "border-current text-current hover:bg-black/10",
                        size: "xs",
                        variant: "ghost",
                    }),
                )}
                data-slot={"button"}
                href={banner.linkUrl}
                rel={"noreferrer"}
                target={"_blank"}
            >
                {t("pay-invoice")}
                <ExternalLinkIcon />
            </a>
        </div>
    );
}
